import { useMediaQuery } from '@mui/material';
import { format, sub, subDays } from 'date-fns';
import { observer } from 'mobx-react-lite';
import { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StorePerformanceInterval } from '../../constants';
import { LocalStorageKey } from '../../constants/localStorageKey';
import {
  PlanProductDetailsResponse,
  SoldProductDetailsResponse,
  StoreSalesPerformanceResponse,
  useAsyncAction,
  useStoreInformation,
  useTransaction,
  useUser,
} from '../../hooks';
import { usePageEventTracker } from '../../hooks/usePageEventTracker';
import { Icon, Icons, Typography } from '../../stories/atoms';
import { ColumnFlexContainer, ContainerV2, RowFlexContainer } from '../../stories/atoms/containerV2/ContainerV2';
import { ActionButton, Banner, BannerItemsType } from '../../stories/molecules';
import { DatePicker } from '../../stories/molecules/date-picker/DatePicker';
import { Page } from '../../stories/organisms';
import { muiTheme } from '../../theme';
import { commonTranslations, homeTranslations, sideMenuTranslations } from '../../translations';
import { getDatePickerLabel, getUniqueId, isFailureResponse } from '../../utils';
import { EquationWidget } from '../home/widgets/storeOverviewWidgets/equationWidget/EquationWidget';
import { getIndexFilterName } from '../home/widgets/storeOverviewWidgets/sales.util';
import { SalesByGarmentGroupWidget } from '../home/widgets/storeOverviewWidgets/salesByGarmentGroupWidget/SalesByGarmentGroupWidget';
import { SalesByIndexWidget } from '../home/widgets/storeOverviewWidgets/salesByIndexWidget/SalesByIndexWidget';
import { SalesPerformanceWidget } from '../home/widgets/storeOverviewWidgets/salesPerformanceWidget/SalesPerformanceWidget';
import { TopPlannedProductWidget } from '../home/widgets/storeOverviewWidgets/topPlannedProductWidget/TopPlannedProductWidget';
import { TopPotentialWidget } from '../home/widgets/storeOverviewWidgets/topPotentialWidget/TopPotentialWidget';
import { TopSoldProductWidget } from '../home/widgets/storeOverviewWidgets/topSoldProductWidget/TopSoldProductWidget';
import { DashboardGrid } from './StoreOverviewPage.styles';
import { StoreOverviewFilters } from './storeOverviewFilters/StoreOverviewFilters';

const ExpandButton: FC<{ isExpanded: boolean; onClick: () => void }> = ({ isExpanded, onClick }) => (
  <RowFlexContainer
    cursor="pointer"
    alignItems="center"
    gap={[1]}
    onClick={onClick}
    hasBorder
    padding={[1, 2]}
    borderRadius={[2]}
  >
    <Typography type="body3">{isExpanded ? 'Close' : 'Expand'}</Typography>
    <Icon icon={Icons.DROPDOWN} rotate={isExpanded ? 'up' : undefined} size="small" />
  </RowFlexContainer>
);

export const StoreOverviewPage: FC<unknown> = observer(() => {
  usePageEventTracker('store_overview');

  const { t } = useTranslation();
  const {
    get: { currentStoreId, userPermissions },
  } = useUser();
  const { storeName, currencyCode, corporateBrandId } = useStoreInformation();
  const isHMBrand = corporateBrandId === 0;
  const newFeatureLocalStorageValue = sessionStorage.getItem(LocalStorageKey.ShowNewFeatureBanner);

  const canViewNewFeatureBanner =
    userPermissions.assetManagementWidget.canRead && newFeatureLocalStorageValue === null;

  const { fetchSalesPerformanceData, fetchWeeklyTopSoldData, fetchWeeklyTopPlannedData } = useTransaction();

  const { datePicker } = homeTranslations;
  const isTablet = useMediaQuery(muiTheme.breakpoints.down('tabletLandscape'));
  const isSmallDesktop = useMediaQuery(muiTheme.breakpoints.down('desktop'));

  // default selected date for the date picker should be today -1
  const [selectedDate, setSelectedDate] = useState(subDays(new Date(), 1));
  const [dateLabel, setDateLabel] = useState(
    getDatePickerLabel(
      selectedDate,
      StorePerformanceInterval.DAILY,
      t(datePicker.weekLabel.key, datePicker.weekLabel.defaultValue),
    ),
  );

  const [selectedIndexFilter, setSelectedIndexFilter] = useState<string | undefined>(undefined);
  const [bannerItems, setBannerItems] = useState<BannerItemsType[]>([]);

  const [isWidgetExpanded, setIsWidgetExpanded] = useState(false);
  const {
    result: salesPerformanceData,
    isLoading: salesPerformanceDataLoading,
    isError: salesperformanceDataError,
    triggerAction: triggerFetchSalesPerformanceData,
  } = useAsyncAction<StoreSalesPerformanceResponse>();

  const {
    result: weeklyTopSoldData,
    isLoading: weeklyTopSoldDataLoading,
    isError: weeklyTopSoldDataError,
    triggerAction: triggerFetchWeeklyTopSoldData,
  } = useAsyncAction<SoldProductDetailsResponse>();

  const {
    result: weeklyTopPlannedData,
    isLoading: weeklyTopPlannedDataLoading,
    isError: weeklyTopPlannedDataError,
    triggerAction: triggerFetchWeeklyTopPlannedData,
  } = useAsyncAction<PlanProductDetailsResponse>();

  // Fetch data
  const fetchSalesData = useCallback(async () => {
    const date = format(selectedDate, 'yyyy-MM-dd');
    const response = await fetchSalesPerformanceData(date, currencyCode);
    if (isFailureResponse(response)) {
      throw new Error();
    }

    return response.data;
  }, [selectedDate, currencyCode]);

  const fetchSoldProductsData = useCallback(async () => {
    const date = format(selectedDate, 'yyyy-MM-dd');
    const response = await fetchWeeklyTopSoldData(date, currencyCode);
    if (isFailureResponse(response)) {
      throw new Error();
    }

    return response.data;
  }, [selectedDate, currencyCode]);

  const fetchPlannedProductsData = useCallback(async () => {
    const date = format(selectedDate, 'yyyy-MM-dd');
    const response = await fetchWeeklyTopPlannedData(date, currencyCode);
    if (isFailureResponse(response)) {
      throw new Error();
    }

    return response.data;
  }, [selectedDate, currencyCode]);

  const triggerFetchSalesData = () => {
    triggerFetchSalesPerformanceData(fetchSalesData);
  };

  const triggerFetchTopSalesData = () => {
    triggerFetchWeeklyTopSoldData(fetchSoldProductsData);
  };

  const triggerFetchTopPlannedData = () => {
    triggerFetchWeeklyTopPlannedData(fetchPlannedProductsData);
  };

  useEffect(() => {
    triggerFetchSalesData();
    triggerFetchTopSalesData();
    triggerFetchTopPlannedData();
  }, [selectedDate]);

  useEffect(() => {
    setBannerItems([
      {
        id: getUniqueId(),
        label: 'Whats New!',
        labelType: 'button',
        content: (
          <ColumnFlexContainer gap={[1]} margin={[1, 0, 0]} padding={[0, 0, 0, 6]}>
            <Typography>
              There are images now available for all products in top sold and top plan products widgets!
            </Typography>
          </ColumnFlexContainer>
        ),
        type: 'info',
        actionButton: ActionButton.Close,
        onClose: () => {
          sessionStorage.setItem(LocalStorageKey.ShowNewFeatureBanner, 'false');
        },
      },
    ]);
  }, []);

  const handleSelectedIndex = (index: string) => {
    setSelectedIndexFilter(index);
  };

  const selectedFilterName = getIndexFilterName(salesPerformanceData, selectedIndexFilter);

  return (
    <Page
      header={
        <RowFlexContainer
          justifyContent="between"
          alignItems="center"
          height="100%"
          padding={isTablet ? [0, 4] : [0, 8]}
        >
          <StoreOverviewFilters
            salesData={salesPerformanceData}
            isLoading={salesPerformanceDataLoading}
            isError={salesperformanceDataError}
            selectedFilter={selectedIndexFilter}
            getSelectedIndex={handleSelectedIndex}
          />
          <DatePicker
            inputSelectedDate={selectedDate}
            onChange={(date) => {
              setSelectedDate(date);
              setSelectedIndexFilter(undefined);
              setDateLabel(
                getDatePickerLabel(
                  date,
                  StorePerformanceInterval.DAILY,
                  t(datePicker.weekLabel.key, datePicker.weekLabel.defaultValue),
                ),
              );
            }}
            disableDatePicker={
              salesPerformanceDataLoading || weeklyTopSoldDataLoading || weeklyTopPlannedDataLoading
            }
            label={dateLabel}
            pickerType="date"
            views={['day']}
            maxDate={sub(new Date(), { days: 1 })}
            showCalendarIcon
          />
        </RowFlexContainer>
      }
      body={
        <div>
          {canViewNewFeatureBanner && (
            <ColumnFlexContainer>
              <Banner
                isSticky
                items={bannerItems}
                onChange={(items) => {
                  setBannerItems(items);
                }}
              />
            </ColumnFlexContainer>
          )}

          <ColumnFlexContainer padding={isTablet ? [6, 4] : [6, 8]} data-testid="store-overview-page-wrapper">
            <Typography type="h3" data-testid="store-name" overflow="visible">
              {storeName + ' ' + `(${currentStoreId})`}
            </Typography>
            <Typography type="body3" overflow="none">
              {t(sideMenuTranslations.storeOverviewPage.key, sideMenuTranslations.storeOverviewPage.defaultValue)}{' '}
              - {dateLabel}
            </Typography>

            {selectedIndexFilter && (
              <RowFlexContainer alignItems="center" cursor="pointer" margin={[2, 0, 0]}>
                <Typography type="body3">
                  {t(commonTranslations.selectedFilters.key, commonTranslations.selectedFilters.defaultValue)} -{' '}
                </Typography>
                <RowFlexContainer hasBorder padding={[1, 2]} gap={[2]} borderRadius={[1]} alignItems="center">
                  <Typography type="body3">{selectedFilterName}</Typography>
                  <Icon icon={Icons.CLOSE} size="small" onClick={() => setSelectedIndexFilter(undefined)} />
                </RowFlexContainer>
              </RowFlexContainer>
            )}
            <ContainerV2 maxHeight="100%" overflow="scroll" margin={[4, 0]}>
              <DashboardGrid $isSmallDesktop={isSmallDesktop} gap={[4]}>
                <SalesPerformanceWidget
                  salesPerformanceData={salesPerformanceData}
                  isLoading={salesPerformanceDataLoading}
                  isError={salesperformanceDataError}
                  filterIndex={selectedIndexFilter}
                  retry={triggerFetchSalesData}
                />
                <EquationWidget
                  salesPerformanceData={salesPerformanceData}
                  isLoading={salesPerformanceDataLoading}
                  isError={salesperformanceDataError}
                  selectedFilter={selectedIndexFilter}
                  retry={triggerFetchSalesData}
                />

                <SalesByIndexWidget
                  salesPerformanceData={salesPerformanceData}
                  isLoading={salesPerformanceDataLoading}
                  isError={salesperformanceDataError}
                  isExpanded={isWidgetExpanded}
                  selectedFilter={selectedIndexFilter}
                  expandButton={
                    <ExpandButton
                      isExpanded={isWidgetExpanded}
                      onClick={() => setIsWidgetExpanded(!isWidgetExpanded)}
                    />
                  }
                  retry={triggerFetchSalesData}
                />

                <SalesByGarmentGroupWidget
                  salesPerformanceData={salesPerformanceData}
                  isLoading={salesPerformanceDataLoading}
                  isError={salesperformanceDataError}
                  isExpanded={isWidgetExpanded}
                  selectedFilter={selectedIndexFilter}
                  expandButton={
                    <ExpandButton
                      isExpanded={isWidgetExpanded}
                      onClick={() => setIsWidgetExpanded(!isWidgetExpanded)}
                    />
                  }
                  retry={triggerFetchSalesData}
                />

                {isHMBrand && (
                  <TopPlannedProductWidget
                    weeklyTopPlannedData={weeklyTopPlannedData}
                    weeklyTopSoldData={weeklyTopSoldData}
                    isPlanProductLoading={weeklyTopPlannedDataLoading}
                    isSoldProductLoading={weeklyTopSoldDataLoading}
                    isError={weeklyTopPlannedDataError}
                    selectedFilter={selectedIndexFilter}
                    retry={triggerFetchTopPlannedData}
                  />
                )}

                {isHMBrand && (
                  <TopSoldProductWidget
                    weeklyTopSoldData={weeklyTopSoldData}
                    isLoading={weeklyTopSoldDataLoading}
                    isError={weeklyTopSoldDataError}
                    selectedFilter={selectedIndexFilter}
                    retry={triggerFetchTopSalesData}
                  />
                )}

                <TopPotentialWidget
                  salesPerformanceData={salesPerformanceData}
                  isLoading={salesPerformanceDataLoading}
                  isError={salesperformanceDataError}
                  selectedFilter={selectedIndexFilter}
                  retry={triggerFetchSalesData}
                />
              </DashboardGrid>
            </ContainerV2>
          </ColumnFlexContainer>
        </div>
      }
    />
  );
});
